import React, { useState } from 'react';
import { useEnhancedExport, ExportOptions as ExportConfig, ImageForExport } from '@/hooks/useEnhancedExport';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Download, FileArchive, File } from 'lucide-react';

interface ExportOptionsProps {
  images: ImageForExport[];
}

export const ExportOptions: React.FC<ExportOptionsProps> = ({ images }) => {
  const { exporting, progress, exportImages } = useEnhancedExport();
  const [config, setConfig] = useState<ExportConfig>({
    format: 'zip',
    namingTemplate: '{name}_{index}',
    folderStructure: 'flat',
    includeMetadata: true,
    includeReport: true,
  });

  const handleExport = async () => {
    if (images.length === 0) return;
    await exportImages(images, config);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Export Options</CardTitle>
        <CardDescription>
          Configure how your images will be exported
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>Export Format</Label>
          <RadioGroup
            value={config.format}
            onValueChange={(value: 'zip' | 'individual') =>
              setConfig(prev => ({ ...prev, format: value }))
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="zip" id="zip" />
              <Label htmlFor="zip" className="font-normal flex items-center gap-2">
                <FileArchive className="h-4 w-4" />
                ZIP Archive (recommended for multiple images)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="individual" id="individual" />
              <Label htmlFor="individual" className="font-normal flex items-center gap-2">
                <File className="h-4 w-4" />
                Individual Downloads
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="naming">File Naming Template</Label>
          <Input
            id="naming"
            value={config.namingTemplate}
            onChange={(e) => setConfig(prev => ({ ...prev, namingTemplate: e.target.value }))}
            placeholder="{name}_{index}_{tool}"
          />
          <p className="text-xs text-muted-foreground">
            Available variables: {'{name}'}, {'{index}'}, {'{tool}'}, {'{date}'}, {'{time}'}
          </p>
        </div>

        {config.format === 'zip' && (
          <div className="space-y-3">
            <Label>Folder Structure</Label>
            <RadioGroup
              value={config.folderStructure}
              onValueChange={(value: 'flat' | 'by-tool' | 'by-date') =>
                setConfig(prev => ({ ...prev, folderStructure: value }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="flat" id="flat" />
                <Label htmlFor="flat" className="font-normal">
                  Flat (all files in root)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="by-tool" id="by-tool" />
                <Label htmlFor="by-tool" className="font-normal">
                  Organize by Tool
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="by-date" id="by-date" />
                <Label htmlFor="by-date" className="font-normal">
                  Organize by Date
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {config.format === 'zip' && (
          <div className="space-y-3">
            <Label>Additional Files</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="metadata"
                  checked={config.includeMetadata}
                  onCheckedChange={(checked) =>
                    setConfig(prev => ({ ...prev, includeMetadata: !!checked }))
                  }
                />
                <Label htmlFor="metadata" className="font-normal">
                  Include metadata.json file
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="report"
                  checked={config.includeReport}
                  onCheckedChange={(checked) =>
                    setConfig(prev => ({ ...prev, includeReport: !!checked }))
                  }
                />
                <Label htmlFor="report" className="font-normal">
                  Include processing report (HTML)
                </Label>
              </div>
            </div>
          </div>
        )}

        {exporting && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Exporting...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        <Button
          onClick={handleExport}
          disabled={images.length === 0 || exporting}
          className="w-full"
        >
          <Download className="mr-2 h-4 w-4" />
          Export {images.length} Image{images.length !== 1 ? 's' : ''}
        </Button>

        {images.length === 0 && (
          <p className="text-sm text-muted-foreground text-center">
            No images to export. Process some images first.
          </p>
        )}
      </CardContent>
    </Card>
  );
};